// IAMMUZIK API client — talks to the real Phase 2-4 backend.
// The base URL works locally (npm run dev) and after deploy_website
// (which rewrites __PORT_8000__ to the proxy path).
import type {
  Notification,
  Song,
  Theme,
  User,
} from '../types';

const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';

let token: string | null =
  typeof window !== 'undefined'
    ? localStorage.getItem('iammuzik_token')
    : null;

export function setToken(t: string | null) {
  token = t;

  if (typeof window !== 'undefined') {
    if (t) {
      localStorage.setItem('iammuzik_token', t);
    } else {
      localStorage.removeItem('iammuzik_token');
    }
  }
}

export function getToken() {
  return token;
}

async function req<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...opts, headers });
  } catch {
    const err = new Error('Network error — could not reach the server.') as any;
    err.status = 0;
    throw err;
  }
  if (!res.ok) {
    let detail: any = { status: res.status };
    try { detail = { ...detail, ...(await res.json()) }; } catch { /* non-json */ }
    const message = detail.message || detail.error || `HTTP ${res.status}`;
    const err = new Error(message) as any;
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  try {
    return await res.json();
  } catch {
    const err = new Error('Server returned an invalid response.') as any;
    err.status = res.status;
    err.detail = { status: res.status };
    throw err;
  }
}

// multipart upload
async function upload<T = any>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', headers, body: form });
  } catch {
    throw Object.assign(new Error('Network error — could not reach the server.'), { status: 0 });
  }
  if (!res.ok) {
    let detail: any = { status: res.status };
    try { detail = { ...detail, ...(await res.json()) }; } catch { /* non-json */ }
    throw Object.assign(new Error(detail.message || detail.error || `HTTP ${res.status}`), { status: res.status, detail });
  }
  try {
    return await res.json();
  } catch {
    throw Object.assign(new Error('Server returned an invalid response.'), { status: res.status });
  }
}

export const api = {
  baseURL: API_BASE,

  // ---- auth ----
  me: () => req<{ user: User }>('/api/auth/me'),
  register: (username: string, email: string, password: string, consent?: { ageGroup: string; termsAccepted: boolean; privacyAccepted: boolean; aiPolicyAccepted: boolean; contentRulesAccepted: boolean; parentalConsent?: boolean; parentalPassword?: string }) =>
    req<{ token: string; user: User }>('/api/auth/register', {
      method: 'POST', body: JSON.stringify({ username, email, password, ...consent }),
    }),
  login: (username: string, password: string) =>
    req<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST', body: JSON.stringify({ username, password }),
    }),
  logout: () => req('/api/auth/logout', {method:'POST'}),
  updateSecurity: (data: {email:string;currentPassword:string;newPassword:string}) => req('/api/auth/security',{method:'POST',body:JSON.stringify(data)}),
  collabs: () => req<{collabs:any[]}>('/api/collabs'),
  createCollab: (data:any) => req('/api/collabs',{method:'POST',body:JSON.stringify(data)}),
  closeCollab: (id:string) => req(`/api/collabs/${id}`,{method:'PATCH',body:JSON.stringify({status:'CLOSED'})}),
  collabInterest: (id:string) => req(`/api/collabs/${id}/interest`,{method:'POST'}),

  // ---- presence ----
  heartbeat: () => req<{ ok: true }>('/api/presence/heartbeat', { method: 'POST' }),
  onlineUsers: () => req<{ users: any[]; count: number }>('/api/presence/online'),

  // songs
  mySongs: () => req<{ songs: Song[]; storageMb: number; limits: any }>('/api/songs'),
  saveSong: (data: any) =>
    req<{ song: Song }>('/api/songs', { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } }),
  uploadSong: (form: FormData) => upload<{ song: Song }>('/api/songs', form),
  patchSong: (id: string, patch: Partial<Song>) =>
    req<{ song: Song }>(`/api/songs/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteSong: (id: string) => req<{ deleted: string }>(`/api/songs/${id}`, { method: 'DELETE' }),
  requestWave: (id: string) => req<{ song: Song }>(`/api/songs/${id}/request-wave`, { method: 'POST' }),

  // wave
  wave: () => req<{ songs: Song[] }>('/api/wave'),
  play: (id: string) => req<{ ok: true }>(`/api/songs/${id}/play`, { method: 'POST' }),

  // report a song
  reportSong: (songId: string, reason: string, details?: string) =>
    req<{ report: any }>('/api/reports', { method: 'POST', body: JSON.stringify({ songId, reason, details }) }),

  // admin
  adminStats: () => req<any>('/api/admin/stats'),
  announcements: () => req<{ announcements: any[] }>('/api/announcements'),
  adminQueue: () => req<{ songs: Song[] }>('/api/admin/queue'),
  approveSong: (id: string) => req<{ song: Song }>(`/api/admin/songs/${id}/approve`, { method: 'POST' }),
  rejectSong: (id: string, reason: string) => req<{ song: Song }>(`/api/admin/songs/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  removeSong: (id: string, reason: string) => req<{ song: Song }>(`/api/admin/songs/${id}/remove`, { method: 'POST', body: JSON.stringify({ reason }) }),
  adminUsers: () => req<{ users: User[] }>('/api/admin/users'),
  adminReports: () => req<{ reports: any[] }>('/api/admin/reports'),
  adminAudit: () => req<{ audit: any[] }>('/api/admin/audit'),
  adminBattles: () => req<{ battles: any[] }>('/api/admin/battles'),
  createBattle: (data: any) => req<{ battle: any }>('/api/admin/battles', { method: 'POST', body: JSON.stringify(data) }),
  updateBattle: (id: string, data: any) => req<{ battle: any }>(`/api/admin/battles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  assignJudges: (battleId: string, judgeIds: string[]) => req<{ battle: any }>(`/api/admin/battles/${battleId}/judges`, { method: 'POST', body: JSON.stringify({ judgeIds }) }),
  adminAiJobs: () => req<{ jobs: any[] }>('/api/admin/ai-jobs'),
  adminOnlineUsers: () => req<{ users: any[]; count: number }>('/api/admin/online'),
  adminPlatformStats: () => req<any>('/api/admin/platform-stats'),
  adminAnnouncements: () => req<{ announcements: any[] }>('/api/admin/announcements'),
  createAnnouncement: (data: any) => req<{ announcement: any }>('/api/admin/announcements', { method: 'POST', body: JSON.stringify(data) }),
  adminSystemHealth: () => req<any>('/api/admin/system-health'),
  suspendUser: (id: string, reason: string, duration: string) => req<{ user: any }>(`/api/admin/users/${id}/suspend`, { method: 'POST', body: JSON.stringify({ reason, duration }) }),
  reactivateUser: (id: string) => req<{ user: any }>(`/api/admin/users/${id}/reactivate`, { method: 'POST' }),
  judgeBattles: () => req<{ battles: any[] }>('/api/judge/battles'),
  judgeScorecards: (battleId: string) => req<any>(`/api/judge/scorecards/${battleId}`),

  // battles
  battles: () => req<{ battles: any[]; genres: string[] }>('/api/battles'),
  battle: (id: string) => req<any>(`/api/battles/${id}`),
  battleQuote: (id: string, categories: number, songs: number) =>
    req<{ cents: number; dollars: string; mode: string }>(`/api/battles/${id}/quote`, { method: 'POST', body: JSON.stringify({ categories, songs }) }),
  enterBattle: (id: string, songId: string, rightsDeclaration: boolean, aiDisclosure: string) =>
    req<{ entry: any; mode: string }>(`/api/battles/${id}/enter`, { method: 'POST', body: JSON.stringify({ songId, rightsDeclaration, aiDisclosure }) }),
  vote: (battleId: string, entryId: string) =>
    req<{ ok: true }>(`/api/battles/${battleId}/vote`, { method: 'POST', body: JSON.stringify({ entryId }) }),
  judgeScore: (data: any) => req<{ scorecard: any }>('/api/judge/scorecards', { method: 'POST', body: JSON.stringify(data) }),
  finalizeBattle: (id: string) => req<any>(`/api/admin/battles/${id}/finalize`, { method: 'POST' }),

  // monthly access codes
  adminAccessCodes: (cycle?: string) => req<{ cycle: string; codes: any[]; verifications: any[]; cycles: string[] }>(`/api/admin/access-codes${cycle ? '?cycle=' + cycle : ''}`),
  generateAccessCodes: (cycle?: string) => req<{ cycle: string; codes: any[] }>('/api/admin/access-codes/generate', { method: 'POST', body: JSON.stringify({ cycle }) }),
  verifyAccessCode: (code: string) => req<{ granted: boolean; kind: string; genre: string | null; cycle: string; message: string }>('/api/access-codes/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  myAccess: () => req<{ cycle: string; judgeAccess: boolean; finalistGenres: string[] }>('/api/access-codes/my-access'),

  // contracts / subscriptions
  contracts: () => req<{ tiers: any[]; mode: string }>('/api/contracts'),
  mySubscription: () => req<{ subscription: any | null; tier: string; mode: string }>('/api/subscriptions/mine'),
  checkout: (tier: string) => req<{ subscription: any; mode: string }>('/api/subscriptions/checkout', { method: 'POST', body: JSON.stringify({ tier }) }),
  cancelSub: () => req<{ subscription: any; mode: string }>('/api/subscriptions/cancel', { method: 'POST' }),
  resumeSub: () => req<{ subscription: any; mode: string }>('/api/subscriptions/resume', { method: 'POST' }),

  // notifications
  notifications: () => req<{ notifications: Notification[]; unreadCount: number }>('/api/notifications'),
  readNotif: (id: string) => req(`/api/notifications/${id}/read`, { method: 'POST' }),
  readAll: () => req('/api/notifications/read-all', { method: 'POST' }),

  // ai instrumental
  instrumentalJob: (data: { prompt: string; genres?: string[]; mood?: string; bpm?: number; duration?: string }) =>
    req<{ job: any }>('/api/ai/instrumental', { method: 'POST', body: JSON.stringify(data) }),

  // ai voice
  voiceJob: (data: { lyrics: string; voiceId?: string; voiceName?: string; voiceCategory?: string; voiceCharacter?: string; performanceStyle?: string[]; performanceDescription?: string; genre?: string; bpm?: number; duration?: string }) =>
    req<{ job: any }>('/api/ai/voice', { method: 'POST', body: JSON.stringify(data) }),

  // mix beat + voice
  mixJob: (data: { beatJobId: string; voiceJobId: string }) =>
    req<{ job: any }>('/api/ai/mix', { method: 'POST', body: JSON.stringify(data) }),

  songJob: (data: { prompt: string; lyrics: string; title?: string; genre?: string; mood?: string; duration?: string }) =>
    req<{ job: any }>('/api/ai/song', { method: 'POST', body: JSON.stringify(data) }),

  videoJob: (data: { prompt: string; songId?: string }) =>
    req<{ job: any }>('/api/ai/video', { method: 'POST', body: JSON.stringify(data) }),

  aiJobStatus: (id: string) => req<{ job: any }>(`/api/ai/jobs/${id}`),

  aiStatus: () => req<{ mode: string; sunoEnabled: boolean; pollyEnabled: boolean; tier: string; quality: any }>('/api/ai/status'),

  aiJobs: () => req<{ jobs: any[] }>('/api/ai/jobs'),

  audioUrl: (fileUrl?: string) => (fileUrl ? `${API_BASE}${fileUrl}` : undefined),

  // ---- Messaging ----
  conversations: () => req<{ conversations: any[] }>('/api/messages/conversations'),
  messages: (conversationId: string) => req<{ messages: any[] }>(`/api/messages/${conversationId}`),
  sendMessage: (conversationId: string, body: string) =>
    req<{ message: any }>(`/api/messages/${conversationId}`, { method: 'POST', body: JSON.stringify({ body }) }),
  startConversation: (username: string) =>
    req<{ conversation: any }>(`/api/messages/conversations`, { method: 'POST', body: JSON.stringify({ username }) }),
  blockUser: (conversationId: string) =>
    req<{ ok: true }>(`/api/messages/${conversationId}/block`, { method: 'POST' }),

  // ---- Live Battle Video ----
  joinLive: (battleId: string) =>
    req<{ token: string | null; url: string; roomName: string; role: string; mode: string }>(`/api/battles/${battleId}/live/join`, { method: 'POST' }),
  leaveLive: (battleId: string) =>
    req<{ ok: true }>(`/api/battles/${battleId}/live/leave`, { method: 'POST' }),
  liveStatus: (battleId: string) =>
    req<{ participants: any[]; chat: any[] }>(`/api/battles/${battleId}/live/status`),
  sendLiveChat: (battleId: string, body: string) =>
    req<{ ok: true }>(`/api/battles/${battleId}/live/chat`, { method: 'POST', body: JSON.stringify({ body }) }),
  generateBattleCodes: (battleId: string) =>
    req<{ codes: any[]; battleTitle: string }>(`/api/battles/${battleId}/generate-codes`, { method: 'POST' }),
  getBattleCodes: (battleId: string) =>
    req<{ codes: any[] }>(`/api/battles/${battleId}/codes`),
  joinWithCode: (battleId: string, code: string) =>
    req<any>(`/api/battles/${battleId}/join-with-code`, { method: 'POST', body: JSON.stringify({ code }) }),
  goLive: (battleId: string) =>
    req<{ ok: true; battle: any }>(`/api/battles/${battleId}/go-live`, { method: 'POST' }),
  activeLive: () => req<{ active: boolean; battles: any[] }>('/api/live/active'),
  userSongs: (userId: string) => req<{ songs: any[]; owner: any }>(`/api/users/${userId}/songs`),

  // ---- Profile / Consent ----
  updateProfile: (data: { dateOfBirth?: string; ageVerified?: boolean; parentalConsent?: boolean; parentalPassword?: string; bio?: string; artistName?: string }) =>
    req<{ user: any }>('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(data) }),

  // ---- Support ----
  submitSupport: (email: string, subject: string, message: string) =>
    req<{ ok: boolean; ticketId: string; message: string }>('/api/support', { method: 'POST', body: JSON.stringify({ email, subject, message }) }),
  adminSupport: () => req<{ tickets: any[]; openCount: number }>('/api/admin/support'),
  updateSupportTicket: (id: string, data: { status?: string; reply?: string }) =>
    req<{ ticket: any }>(`/api/admin/support/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // ---- Parental consent verification ----
  verifyParental: (password: string) =>
    req<{ ok: true }>('/api/auth/verify-parental', { method: 'POST', body: JSON.stringify({ password }) }),
};

export function loadTheme(): Theme {
  return 'dark';
}
